import * as url from "helpers/url_helper";
import { APIClient } from "helpers/api_helper";
import { postImport } from "common/lib/importClient";

const api = new APIClient();

export const getContractors = (params: any) =>
  api.get(url.GET_CONTRACTORS, params);
export const getContractor = (id: any, params: any) => {
  const populate: any[] = [];
  if (!params?.populate) params.populate = populate;
  return api.get(url.GET_CONTRACTOR + "/" + id, params);
};
export const addContractor = (data: any) =>
  api.create(url.ADD_CONTRACTOR, data);
export const updateContractor = (data: any) =>
  api.update(url.UPDATE_CONTRACTOR + "/" + data.id, data);
export const deleteContractor = (data: any) =>
  api.delete(url.DELETE_CONTRACTOR + "/" + data.id, data);
export const importContractor = (
  data: any,
  accessToken: string,
  options?: { timeoutMs?: number }
) => {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  if (options?.timeoutMs) {
    timeoutId = setTimeout(() => controller.abort(), options.timeoutMs);
  }

  return fetch(process.env.REACT_APP_API_URL + url.IMPORT_CONTRACTOR, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
    signal: controller.signal,
  })
    .then(async (response) => {
      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Get the X-Metadata header if it exists
      const metadata = response.headers.get("X-Metadata");
      let parsedMetadata = null;

      // Try to parse the metadata if it exists
      if (metadata) {
        try {
          parsedMetadata = JSON.parse(metadata);
        } catch (e) {
          parsedMetadata = metadata;
        }
      }

      // Get the blob regardless of content type
      const blob = await response.blob();

      // Return both the blob and metadata
      return {
        blob,
        metadata: parsedMetadata,
      };
    })
    .finally(() => {
      if (timeoutId !== null) clearTimeout(timeoutId);
    });
};
export const exportContractorsToCSV = (params: any) =>
  api.get(url.EXPORT_CONTRACTORS_TO_CSV, params, {
    responseType: "blob",
  });
export const exportAgencyContractorsToCSV = (params: any) =>
  api.get(url.EXPORT_AGENCY_CONTRACTORS_TO_CSV, params, {
    responseType: "blob",
  });
export const mergeContractors = (data: {
  id: string; // id of selected contractor
  contractorId: string; // id of contractor to merge
}) => api.update(url.MERGE_CONTRACTORS + "/" + data.id + "/merge", data);

export const getAgencyContractors = (params: any) =>
  api.get(url.GET_AGENCY_CONTRACTORS, params);
export const getAgencyContractor = (id: any, params: any) =>
  api.get(url.GET_AGENCY_CONTRACTOR + "/" + id, params);
export const addAgencyContractor = (data: any) =>
  api.create(url.ADD_AGENCY_CONTRACTOR, data);
export const updateAgencyContractor = (data: any) =>
  api.update(url.UPDATE_AGENCY_CONTRACTOR + "/" + data.id, data);
export const deleteAgencyContractor = (data: any) =>
  api.delete(url.DELETE_AGENCY_CONTRACTOR + "/" + data.id, data);

export const getContractorSummary = (params: any) =>
  api.get(url.GET_CONTRACTOR_SUMMARY + "/" + params.id);

export const updateContractorSEMG = (data: any) =>
  api.update(
    url.UPDATE_CONTRACTOR_SEMG_MENTION + "/" + data.id + "/semg-mention",
    data
  );

export const updateContractorPerformanceRating = (data: any) =>
  api.update(
    url.UPDATE_CONTRACTOR_PERFORMANCE_RATING +
      "/" +
      data.id +
      "/performance-rating",
    data
  );

export const updateContractorBoardOfDirectors = (data: any) =>
  api.update(
    url.UPDATE_CONTRACTOR_BOARD_OF_DIRECTORS +
      "/" +
      data.id +
      "/board-of-directors",
    data
  );

export const updateContractorAffiliationsToPerson = (data: any) =>
  api.update(
    url.UPDATE_CONTRACTOR_AFFILIATIONS_TO_PERSON +
      "/" +
      data.id +
      "/affiliations-to-persons",
    data
  );

export const updateContractorAffiliationsToContractor = (data: any) =>
  api.update(
    url.UPDATE_CONTRACTOR_AFFILIATIONS_TO_CONTRACTOR +
      "/" +
      data.id +
      "/affiliations-to-contractors",
    data
  );

export const getOrganizationTypes = (params: any) =>
  api.get(url.GET_ORGANIZATION_TYPES, params);
export const getOrganizationType = (id: any, params: any) =>
  api.get(url.GET_ORGANIZATION_TYPE + "/" + id, params);
export const addOrganizationType = (data: any) =>
  api.create(url.ADD_ORGANIZATION_TYPE, data);
export const updateOrganizationType = (data: any) =>
  api.update(url.UPDATE_ORGANIZATION_TYPE + "/" + data.id, data);
export const deleteOrganizationType = (id: any) =>
  api.delete(url.DELETE_ORGANIZATION_TYPE + "/" + id);
export const exportOrganizationTypesToCSV = (params: any) =>
  api.get(url.EXPORT_ORGANIZATION_TYPES_TO_CSV, params, { responseType: "blob" });
export const importOrganizationType = (
  data: any,
  accessToken: string,
  options?: { timeoutMs?: number }
) => postImport(url.IMPORT_ORGANIZATION_TYPE, data, accessToken, options);

// Contractor Duplicates
export const scanContractorDuplicates = () =>
  api.create(url.SCAN_CONTRACTOR_DUPLICATES, {});
export const getContractorDuplicateScanStatus = () =>
  api.get(url.GET_CONTRACTOR_DUPLICATE_SCAN_STATUS, {});
export const getContractorDuplicates = (params: any) =>
  api.get(url.GET_CONTRACTOR_DUPLICATES, params);
export const resolveContractorDuplicate = (id: any, data: any) =>
  api.create(url.RESOLVE_CONTRACTOR_DUPLICATE + "/" + id + "/resolve", data);
export const bulkResolveContractorDuplicates = (data: {
  ids: Array<string | number>;
  status: "duplicate" | "not_duplicate";
  note?: string;
}) => api.create(url.BULK_RESOLVE_CONTRACTOR_DUPLICATES, data);
